import { useEffect, useRef } from 'react';
import {
  Outlet,
  useParams,
  generatePath,
  useMatch,
  useNavigate,
  NavLink,
  useOutletContext,
} from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import {
  Tabs,
  IconLoader,
  IconArrowLeft,
  PermissionDenied,
  PermissionEnum,
} from '@ui/src';
import { useAppSelector } from '@store';
import { OrganizationType } from '../../common/types';
import CustomerRightSide from '../../../components/common/info-right-side/info-right-side';
import CorporateRightSideContent from '../../../components/corporate/right-side-content';
import useInformation from './hooks/use-information';
import useGetOrganizationTabs from './hooks/use-get-organization-tabs';

import ROUTES from '../../../routes';

import commonStyles from '../../common/common.module.css';

const CorporateCustomerInformation = () => {
  const { customerId } = useParams();
  const { aml } = useAppSelector((state) => state.customerAml);
  const { baseInformation, totalBalance, mainChat } = useAppSelector(
    (state) => state.corporateCustomers.info.general,
  );
  const { topUpOptions } = useAppSelector((state) => state.topUpPanel);
  const { companyOfficial } = useAppSelector(
    (state) => state.corporateCustomers.info.overview,
  );
  const { cardsEnabled } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const { activePermissions } = useAppSelector((state) => state.root);

  const { sendMessage } = useOutletContext<{
    sendMessage: SendMessage | null;
  }>();

  const tabsWrapperRef = useRef<HTMLDivElement | null>(null);
  const tabsRef = useRef<HTMLDivElement | null>(null);

  const match = useMatch(ROUTES.CUSTOMERS_CORPORATE_PAGE);
  const navigate = useNavigate();

  const { isLoading, isFetching, notFound, query, onClear } = useInformation({
    customerId,
    activePermissions,
    baseInformation,
    topUpOptions,
  });

  const { tabs, hiddenRoutes, withOverflow } = useGetOrganizationTabs({
    customerId,
    cardsEnabled,
    activePermissions,
    baseInformation,
    isLoading,
    tabsWrapperRef,
    tabsRef,
  });

  useEffect(() => {
    if (match && customerId) {
      navigate(
        generatePath(ROUTES.CUSTOMERS_CORPORATE_OVERVIEW, { customerId }),
      );
    }
  }, [match, customerId]);

  useEffect(() => {
    let title = baseInformation?.name || 'Corporate Customers';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [baseInformation?.name]);

  let content;

  if (isLoading || (isFetching && !baseInformation)) {
    content = (
      <div className={commonStyles.customerLoader}>
        <IconLoader />
      </div>
    );
  } else if (!isLoading && notFound) {
    content = (
      <div className={commonStyles.userNotFound}>
        <div className={commonStyles.userNotFoundTop}>
          <NavLink
            to={`${ROUTES.CUSTOMERS_CORPORATE_LIST}${query || ''}`}
            className={commonStyles.backLink}
            onClick={() => onClear()}
          >
            <IconArrowLeft width={13} height={11} />
            Back
          </NavLink>
        </div>
        <div
          className={commonStyles.userNotFoundContent}
        >{`Client with identifier ${customerId} not found`}</div>
      </div>
    );
  } else if (
    !isLoading &&
    activePermissions?.includes(PermissionEnum.CORPORATE_CUSTOMERS_LIST) &&
    !notFound
  ) {
    content = (
      <>
        <div className={commonStyles.customerLeft}>
          <div className={commonStyles.linkWrapper}>
            <NavLink
              to={`${ROUTES.CUSTOMERS_CORPORATE_LIST}${query || ''}`}
              className={commonStyles.backLink}
              onClick={() => onClear()}
            >
              <IconArrowLeft width={13} height={11} />
              Back
            </NavLink>
          </div>
          <div
            className={commonStyles.customerTabsWrapper}
            ref={tabsWrapperRef}
          >
            <Tabs
              tabs={tabs}
              hiddenRoutes={hiddenRoutes}
              itemClassName={commonStyles.customerTab}
              wrapperClassName={`${commonStyles.customerTabs} ${withOverflow ? commonStyles.tabsWithOverflow : ''}`}
              wrapperRef={tabsRef}
              enableRouting
            />
          </div>
          <Outlet context={{ sendMessage }} />
        </div>
        <div className={commonStyles.customerRight}>
          <CustomerRightSide
            customerId={customerId as string}
            risk={aml?.totalRiskScore}
            balance={totalBalance?.balance as number}
            tariff={baseInformation?.tariff as string}
            customerType={OrganizationType.CORPORATE}
            name={baseInformation?.name}
            mainChat={mainChat}
            status={baseInformation?.status}
          >
            <CorporateRightSideContent
              name={baseInformation?.name as string}
              registrationNumber={baseInformation?.registrationNumber as string}
              owner={companyOfficial?.find(
                (item) =>
                  item.companyOfficialType === 'MAIN_REPRESENTATIVE' &&
                  item.status === 'APPROVED',
              )}
              sic={baseInformation?.sic as string}
              countryOfIncorporated={
                baseInformation?.countryOfIncorporated as string
              }
            />
          </CustomerRightSide>
        </div>
      </>
    );
  } else {
    content = <PermissionDenied />;
  }

  return <div className={commonStyles.customerPageContent}>{content}</div>;
};

export default CorporateCustomerInformation;
